<template>
    <div class="billing-modes-container">
        <p class="section-desc">{{ $t('settings.billingModes.description') }}</p>

        <div v-if="loading" class="loading-state">
            <b-spinner variant="primary"></b-spinner>
        </div>

        <div v-else>
            <div
                v-for="group in groupedItems"
                :key="group.key"
                class="group-card"
            >
                <div class="group-header">
                    <div class="group-icon-wrap">
                        <b-icon :icon="group.icon" font-scale="1.1"></b-icon>
                    </div>
                    <div class="group-meta">
                        <span class="group-name">{{ $t(group.labelKey) }}</span>
                        <span class="group-count">{{ group.items.length }} {{ $t('settings.billingModes.services') }}</span>
                    </div>
                </div>

                <div class="group-rows">
                    <div
                        v-for="item in group.items"
                        :key="item.code"
                        class="mode-row"
                        :class="{ 'is-overridden': item.selectedMode !== 'default' }"
                    >
                        <div class="mode-service">
                            <span class="service-label">{{ item.label }}</span>
                            <code class="service-code">{{ item.code }}</code>
                        </div>
                        <div class="mode-controls">
                            <b-form-select
                                v-model="item.selectedMode"
                                :options="modeOptionsForItem(item)"
                                size="sm"
                                class="mode-select"
                            />
                            <b-badge
                                :variant="item.selectedMode !== 'default' ? 'info' : 'secondary'"
                                class="status-badge"
                            >
                                {{ item.selectedMode !== 'default' ? $t('settings.billingModes.overridden') : ($t('settings.billingModes.platformDefault') + (item.platformDefault ? ' (' + item.platformDefault + ')' : '')) }}
                            </b-badge>
                        </div>
                    </div>
                </div>
            </div>

            <div class="save-row">
                <b-button
                    variant="success"
                    @click="saveChanges"
                    :disabled="saving || !hasPendingChanges"
                >
                    <b-spinner small v-if="saving"></b-spinner>
                    <span v-else>{{ $t('save') }}</span>
                </b-button>
                <span v-if="hasPendingChanges" class="pending-label">
                    {{ $t('settings.billingModes.unsavedChanges') }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
var OPTION_CODES = [
    { code: 'kortex-prompt-tokens',     labelKey: 'billingModeOptions.kortexPromptTokens' },
    { code: 'kortex-completion-tokens', labelKey: 'billingModeOptions.kortexCompletionTokens' },
    { code: 'kortex-ocr-seconds',       labelKey: 'billingModeOptions.kortexOcrSeconds' },
    { code: 'docs-examined',            labelKey: 'billingModeOptions.docsExamined' },
    { code: 'bytes-returned',           labelKey: 'billingModeOptions.bytesReturned' },
    { code: 'platform',                 labelKey: 'billingModeOptions.platform' },
    { code: 'public',                   labelKey: 'billingModeOptions.public' },
]

var GROUPS = [
    {
        key: 'kortex',
        labelKey: 'billingModeGroups.kortex',
        icon: 'cpu',
        codes: ['kortex-prompt-tokens', 'kortex-completion-tokens', 'kortex-ocr-seconds'],
    },
    {
        key: 'data',
        labelKey: 'billingModeGroups.data',
        icon: 'file-earmark-text',
        codes: ['docs-examined', 'bytes-returned'],
    },
    {
        key: 'platform',
        labelKey: 'billingModeGroups.platform',
        icon: 'grid',
        codes: ['platform', 'public'],
    },
]

export default {
    name: 'BillingModes',
    data: function () {
        return {
            loading: false,
            saving: false,
            items: [],
            originalModes: {},
            platformDefaults: {},
            allowedModes: {},
        }
    },
    computed: {
        modeOptions: function () {
            return [
                { value: 'default', text: this.$t('settings.billingModes.modeDefault') },
                { value: 'credits', text: this.$t('settings.billingModes.modeCredits') },
                { value: 'hybrid',  text: this.$t('settings.billingModes.modeHybrid') },
                { value: 'invoice', text: this.$t('settings.billingModes.modeInvoice') },
            ]
        },
        groupedItems: function () {
            var self = this
            return GROUPS.map(function (group) {
                return {
                    key: group.key,
                    labelKey: group.labelKey,
                    icon: group.icon,
                    items: self.items.filter(function (item) {
                        return group.codes.indexOf(item.code) !== -1
                    }),
                }
            }).filter(function (group) {
                return group.items.length > 0
            })
        },
        hasPendingChanges: function () {
            for (var i = 0; i < this.items.length; i++) {
                var item = this.items[i]
                var original = this.originalModes[item.code] || 'default'
                if (item.selectedMode !== original) {
                    return true
                }
            }
            return false
        },
    },
    methods: {
        modeOptionsForItem: function (item) {
            var self = this
            var base = this.modeOptions
            return base.filter(function (opt) {
                if (opt.value === 'default') return true
                return item.allowedModes.indexOf(opt.value) !== -1
            })
        },

        loadModes: function () {
            this.loading = true
            var self = this
            this.$transactionApi.get('/billing/billing-modes').then(function (resp) {
                var modes     = (resp.data && resp.data.usage_billing_modes) ? resp.data.usage_billing_modes : {}
                var defaults  = (resp.data && resp.data.platform_defaults)   ? resp.data.platform_defaults   : {}
                var allowed   = (resp.data && resp.data.allowed_modes)      ? resp.data.allowed_modes        : {}
                self.platformDefaults = defaults
                self.allowedModes = allowed
                self.buildItems(modes, defaults, allowed)
            }).catch(function () {
                self.$bvToast.toast(self.$t('tryAgainLater'), {
                    variant: 'danger',
                    title: self.$t('error'),
                })
            }).finally(function () {
                self.loading = false
            })
        },

        buildItems: function (modes, defaults, allowed) {
            var self = this
            self.originalModes = {}
            self.items = OPTION_CODES.map(function (opt) {
                var current = (modes && modes[opt.code]) ? modes[opt.code] : 'default'
                var itemAllowed = (allowed && allowed[opt.code]) ? allowed[opt.code] : ['credits', 'hybrid', 'invoice']
                // If the current override is no longer allowed, reset to default.
                if (current !== 'default' && itemAllowed.indexOf(current) === -1) {
                    current = 'default'
                }
                self.originalModes[opt.code] = current
                return {
                    code: opt.code,
                    label: self.$t(opt.labelKey),
                    selectedMode: current,
                    platformDefault: (defaults && defaults[opt.code]) ? defaults[opt.code] : 'invoice',
                    allowedModes: itemAllowed,
                }
            })
        },

        saveChanges: function () {
            var self = this
            var tasks = []

            for (var i = 0; i < this.items.length; i++) {
                var item = this.items[i]
                var original = this.originalModes[item.code] || 'default'
                if (item.selectedMode === original) continue

                if (item.selectedMode === 'default') {
                    tasks.push({ action: 'delete', code: item.code })
                } else {
                    tasks.push({ action: 'put', code: item.code, mode: item.selectedMode })
                }
            }

            if (tasks.length === 0) return

            self.saving = true
            var pending = tasks.length
            var errors = 0

            function done() {
                pending--
                if (pending === 0) {
                    self.saving = false
                    if (errors === 0) {
                        self.$bvToast.toast(self.$t('settings.billingModes.saveSuccess'), {
                            variant: 'success',
                            title: self.$t('success'),
                        })
                        self.loadModes()
                    } else {
                        self.$bvToast.toast(self.$t('settings.billingModes.saveFailed'), {
                            variant: 'danger',
                            title: self.$t('error'),
                        })
                    }
                }
            }

            for (var j = 0; j < tasks.length; j++) {
                var task = tasks[j]
                if (task.action === 'put') {
                    this.$transactionApi.put('/billing/billing-modes', {
                        option_code: task.code,
                        mode: task.mode,
                    }).then(function () { done() }).catch(function () { errors++; done() })
                } else {
                    this.$transactionApi.delete('/billing/billing-modes', {
                        data: { option_code: task.code },
                    }).then(function () { done() }).catch(function () { errors++; done() })
                }
            }
        },
    },
    created: function () {
        this.loadModes()
    },
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.billing-modes-container {
  padding: 8px 0;
}

.section-desc {
  color: var(--text-secondary);
  margin-bottom: 20px;
  font-size: 0.95rem;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

/* ── Group card ─────────────────────────────────────── */
.group-card {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid $dark-border2;
  background: $dark-hard2-background;
}

.group-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(102, 126, 234, 0.15);
  color: $protokol-royal-active;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.group-meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.group-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text-primary);
}

.group-count {
  font-size: 0.78rem;
  color: var(--text-muted);
}

/* ── Mode row ────────────────────────────────────────── */
.mode-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid $dark-border2;
  transition: background 0.15s;
  gap: 16px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &.is-overridden {
    background: rgba(102, 126, 234, 0.04);
  }
}

.mode-service {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.service-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.service-code {
  font-size: 0.75rem;
  color: var(--text-muted);
  background: rgba(128, 128, 128, 0.12);
  border-radius: 4px;
  padding: 1px 6px;
  font-family: 'SFMono-Regular', Consolas, monospace;
  align-self: flex-start;
}

.mode-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.mode-select {
  width: 190px;
  background: $dark-hard2-background;
  border: 1px solid $dark-border2;
  color: var(--text-primary);
  border-radius: 8px;
  font-size: 0.875rem;

  &:focus {
    background: $dark-hard2-background;
    border-color: $protokol-royal-active;
    color: var(--text-primary);
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }

  option {
    background: $dark-hard2-background;
    color: var(--text-primary);
  }
}

.status-badge {
  font-size: 0.72rem;
  font-weight: 500;
  padding: 0.3em 0.7em;
  white-space: nowrap;
}

/* ── Save row ────────────────────────────────────────── */
.save-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.pending-label {
  font-size: 0.875rem;
  color: var(--text-muted);
}

/* ── Responsive ─────────────────────────────────────── */
@media (max-width: 600px) {
  .mode-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .mode-controls {
    width: 100%;
    justify-content: space-between;
  }

  .mode-select {
    width: 160px;
  }
}
</style>
